import { useCallback, useEffect, useMemo, useRef } from 'react';

import { DEFAULT_AMOUNT_OF_LOGS_TO_SHOW, LIMIT_AUDITS } from '../constants';
import type { UsePaginationProps, UsePaginationResponse } from './types';

export const usePagination = ({
  filteredChangeLogList,
  total,
  changeLogEntries,
  setQueryVariables,
  setDesiredItemsToShow,
  desiredItemsToShow,
}: UsePaginationProps): UsePaginationResponse => {
  const isInitialMount = useRef(true);

  const filteredChangeLogListCount = filteredChangeLogList.length;
  const changeLogEntriesCount = changeLogEntries.length;
  const hasMore = changeLogEntriesCount === total && filteredChangeLogList.length < desiredItemsToShow ? false : true;

  const filteredLogsToShow = useMemo(() => filteredChangeLogList.slice(0, desiredItemsToShow), [
    desiredItemsToShow,
    filteredChangeLogList,
  ]);

  const getMoreLogs = useCallback(() => {
    /*We have to check filteredChangeLogList.length < desiredItemsToShow + 1
    since we need previous log to build proper change log for some fields. For instance - day part field.
     It is needed in the next files: app/src/common/ChangeLog/ChangeLogList/useChangeLogList.tsx
     You could find the next code where previous log needed:
         const previousDayPart = (changeLogEntries[index + 1].node.audits.object as AuditLineItemV5)?.schedule
            ?.dayPartList;
         previousObject = changeLogEntries[index + 1].node.audits.object;
     */
    if (hasMore && filteredChangeLogListCount < desiredItemsToShow + 1 && total! > changeLogEntriesCount) {
      setQueryVariables((prevVariables) => ({
        ...prevVariables,
        paginationOptions: {
          limit: prevVariables.paginationOptions.limit + LIMIT_AUDITS,
          offset: 0,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeLogEntriesCount, desiredItemsToShow]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }

    getMoreLogs();
  }, [changeLogEntriesCount, desiredItemsToShow, hasMore, getMoreLogs]);

  const onNext = useCallback((): void => {
    setDesiredItemsToShow(desiredItemsToShow + DEFAULT_AMOUNT_OF_LOGS_TO_SHOW);
  }, [desiredItemsToShow, setDesiredItemsToShow]);

  return { filteredLogsToShow, hasMore, onNext };
};
